'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'sl' | 'en';

interface LanguageContextType {
  currentLang: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [currentLang, setCurrentLang] = useState<Language>('sl');
  const [isClient, setIsClient] = useState(false);

  // Set client flag after mount to prevent SSR issues
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Load language from localStorage on mount (client-side only)
  useEffect(() => {
    if (!isClient) return;

    try {
      const savedLang = localStorage.getItem('language') as Language;
      const validLanguages: Language[] = ['sl', 'en'];
      if (savedLang && validLanguages.includes(savedLang)) {
        setCurrentLang(savedLang);
      }
    } catch (error) {
      console.error('Error loading language from localStorage:', error);
    }
  }, [isClient]);

  const setLanguage = (lang: Language) => {
    setCurrentLang(lang);
    if (isClient) {
      try {
        localStorage.setItem('language', lang);
      } catch (error) {
        console.error('Error saving language to localStorage:', error);
      }
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLang, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
